/**
 * Inventory module: monthly chips, coins, and books inventory.
 * Three tabs: Plastic Chips, Bronze Coins, Books.
 * Submits report and triggers email with Low Inventory Alert + Full Summary.
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';

const TAB_KEYS = ['chips', 'coins', 'books'];

const InventoryPage = () => {
  const [config, setConfig] = useState(null);
  const [activeTab, setActiveTab] = useState('chips');
  const [reportDate, setReportDate] = useState('');
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  useEffect(() => {
    get('/api/inventory/config').then(({ ok, data }) => {
      if (ok && data?.sections) setConfig(data);
    });
  }, []);

  const handleChange = (id, value) => {
    const num = value === '' ? '' : Math.max(0, parseInt(value, 10));
    if (num !== '' && isNaN(num)) return;
    setFormData((prev) => ({ ...prev, [id]: num }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!reportDate) {
      setError('Please select a report date.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = { reportDate, ...formData };
      const { ok, status, data } = await post('/api/inventory', payload);

      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to submit inventory.'));
        return;
      }
      setSubmitSuccess(true);
      setFormData({});
      setReportDate('');
    } catch (err) {
      setError('Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="entry-page">
        <div className="entry-success-card">
          <div className="success-icon">✓</div>
          <h2>Inventory Report Submitted!</h2>
          <p style={{ color: 'var(--cr-text-muted)', margin: '0 0 1.5rem 0' }}>
            Report saved and email sent to designated recipients.
          </p>
          <div className="success-buttons">
            <Link to="/inventory/history" className="btn-primary">View Past Reports</Link>
            <button type="button" onClick={() => setSubmitSuccess(false)} className="btn-primary">Submit Another Report</button>
            <Link to="/gatherings" className="btn-outline">Back to Gatherings</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="entry-page">
        <p style={{ color: 'var(--cr-text-muted)' }}>Loading inventory form…</p>
      </div>
    );
  }

  const sections = config.sections;

  return (
    <div className="entry-page">
      <PageHeader title="Inventory Report" section="gatherings" />
      <p style={{ fontSize: '0.95rem', color: 'var(--cr-text-muted)', marginBottom: '1rem', textAlign: 'center' }}>
        Monthly chips, coins, and books. Run anytime; report emailed on submit.
      </p>

      <div style={{ backgroundColor: 'var(--cr-bg-warm)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid var(--cr-border)', width: '100%', maxWidth: '520px' }}>
        <button
          type="button"
          id="inventory-report-instructions-toggle"
          aria-expanded={instructionsOpen}
          aria-controls="inventory-report-instructions"
          onClick={() => setInstructionsOpen((o) => !o)}
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
            color: 'var(--cr-text)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.35rem 0.5rem',
          }}
        >
          <span aria-hidden>{instructionsOpen ? '▼' : '▶'}</span>
          <strong>Instructions</strong>
          <span style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', fontWeight: 400 }}>
            {instructionsOpen ? '(hide)' : '(tap to expand)'}
          </span>
        </button>
        {instructionsOpen && (
          <ol id="inventory-report-instructions" style={{ paddingLeft: '1.2rem', margin: '0.75rem 0 0 0' }}>
            <li><strong>Select a report date.</strong> Choose the month you are reporting on. This is typically the last day of the month you took inventory.</li>
            <li><strong>Enter counts for each section.</strong> Use the tabs to switch between Plastic Chips, Bronze Coins, and Books. For each item, enter the current count. The minimum stock level is shown next to each item.</li>
            <li><strong>Submit the report.</strong> Click &quot;Submit Inventory Report&quot; when done. The report is saved and an email is sent to designated recipients. Items below minimum will be flagged in the email.</li>
          </ol>
        )}
      </div>

      <p style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <Link to="/inventory/history" style={{ fontSize: '0.9rem', color: 'var(--cr-link)', fontWeight: 500 }}>
          View past reports →
        </Link>
      </p>

      <form onSubmit={handleSubmit} className="inventory-form" style={{ width: '100%', maxWidth: '520px' }}>
        <div className="form-group" style={{ marginBottom: '1.25rem' }}>
          <label htmlFor="inventory-report-date">Report Date (month of inventory):</label>
          <input
            id="inventory-report-date"
            type="date"
            value={reportDate}
            onChange={(e) => setReportDate(e.target.value)}
            onClick={(e) => e.target.showPicker?.()}
            required
          />
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--cr-border)' }}>
          {TAB_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              style={{
                padding: '0.5rem 1rem',
                background: activeTab === key ? 'var(--cr-button)' : 'transparent',
                color: activeTab === key ? 'var(--white)' : 'var(--cr-text)',
                border: `1px solid ${activeTab === key ? 'var(--cr-button)' : 'var(--cr-border)'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: activeTab === key ? 600 : 500
              }}
            >
              {sections[key]?.label || key}
            </button>
          ))}
        </div>

        <div className="inventory-input-rows" style={{ marginBottom: '1.5rem', minHeight: '200px' }}>
          {(sections[activeTab]?.items || []).map((item) => (
            <div key={item.id} className="inventory-input-row">
              <label className="inventory-input-label" htmlFor={`inventory-qty-${item.id}`}>{item.name} <span>(min: {item.minStock})</span></label>
              <input
                id={`inventory-qty-${item.id}`}
                type="number"
                min={0}
                value={formData[item.id] ?? ''}
                onChange={(e) => handleChange(item.id, e.target.value)}
                placeholder="0"
                className="inventory-input"
              />
            </div>
          ))}
        </div>

        {error && <p style={{ color: 'var(--cr-text)', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Submit Inventory Report'}
        </button>
      </form>
    </div>
  );
};

export default InventoryPage;
